import { useSettingsStore } from "@/stores";

import { getLowestLevel, getNextLevel } from "./level";
import SearchRequestMethods from "./search-request-helper";

export const MONOLINGUAL = "monolingual";
export const BILINGUAL = "bilingual";

const Dictionaries = {
  /**
   * @returns {Array<object>}
   */
  get list() {
    return useSettingsStore().preferredPackageGroupDictionaries;
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {"beginner"|"intermediate"|"expert"|undefined}
   */
  getLevelFromDictionaryId(dictionaryId) {
    const dictionary = this.list.find((d) => d.dictionaryId === dictionaryId);

    // Bilingual are only available from the level the user is and up
    if (dictionary === undefined) {
      return undefined;
    }

    const levels = Object.keys(dictionary.level);
    return getLowestLevel(levels);
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {string}
   */
  getTypeFromDictionaryId(dictionaryId) {
    return this.list.find((d) => d.dictionaryId === dictionaryId)?.type;
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {boolean}
   */
  getDropdownSupportByDictionaryId(dictionaryId) {
    return !["ordbogen-klkl"].includes(dictionaryId);
  },

  /**
   * This assumes all dictionary for that direction have the same direction support.
   *
   * @param {string} direction
   *
   * @returns {boolean}
   */
  getDirectionsSupportByDirection(direction) {
    return !["daar", "arda"].includes(direction);
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {boolean}
   */
  getDictionaryLabelSupportByDictionaryId(dictionaryId) {
    return dictionaryId === "ordbogen-klkl";
  },

  /**
   * @param {string} languageGroup
   * @param {string} direction
   * @param {string} level
   *
   * @returns {Array<string>}
   */
  getMethodIdsByLanguageGroupAndDirectionAndLevel(languageGroup, direction, level) {
    return this.list
      .filter((dictionary) => {
        if (dictionary.languageGroup !== languageGroup) {
          return false;
        }

        if (dictionary.level[level] === undefined) {
          return false;
        }

        if (dictionary.direction === direction) {
          return true;
        }

        return dictionary.direction.slice(-4, -2) === direction.slice(-4, -2);
      })
      .map((dictionary) => dictionary.methodId);
  },

  /**
   * @param {string} languageGroup
   * @param {string} direction
   * @param {string} level
   *
   * @returns {SearchRequestMethods}
   */
  getSearchRequestMethodsFromLanguageGroupAndDirectionAndLevel(languageGroup, direction, level) {
    const searchRequestMethods = new SearchRequestMethods();

    do {
      const directionMethodIds = this.getMethodIdsByLanguageGroupAndDirectionAndLevel(
        languageGroup,
        direction,
        level
      );

      searchRequestMethods.add(level, direction, directionMethodIds);

      const reverseDirection = this.getReverseDirectionByDirectionAndLevel(direction, level);

      if (reverseDirection !== direction) {
        const reverseMethodIds = this.getMethodIdsByLanguageGroupAndDirectionAndLevel(
          languageGroup,
          reverseDirection,
          level
        );

        searchRequestMethods.add(level, reverseDirection, reverseMethodIds);
      }

      level = getNextLevel(level);
    } while (level !== null);

    return searchRequestMethods;
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {import("@/types/dictionary").Dictionary | undefined}
   */
  getDictionaryByDictionaryId(dictionaryId) {
    return this.list.find((d) => d.dictionaryId === dictionaryId);
  },

  /**
   * @param {string} methodId
   *
   * @returns {string|undefined}
   */
  getDictionaryIdByMethodId(methodId) {
    return this.list.find((d) => d.methodId === methodId)?.dictionaryId;
  },

  /**
   * @param {string} languageGroup
   * @param {string} level
   *
   * @returns {import("@/types/dictionary").Dictionary | undefined}
   */
  getFirstDictionaryFromLanguageGroupAndLevel(languageGroup, level) {
    do {
      const dictionary = this.list.find((d) => d.languageGroup === languageGroup && d.level[level]);

      if (dictionary) {
        return dictionary;
      }

      level = getNextLevel(level);
    } while (level !== null);
  },

  /**
   * @param {string} direction
   *
   * @returns {Array<object>}
   */
  getDictionariesByDirection(direction) {
    return this.list.filter((d) => d.direction === direction);
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {string}
   */
  getFirstDirectionByDictionaryId(dictionaryId) {
    return this.list.find((d) => d.dictionaryId === dictionaryId)?.direction ?? "";
  },

  /**
   * @param {string} dictionaryId
   *
   * @returns {Array<string>}
   */
  getDirectionsByDictionaryId(dictionaryId) {
    return this.list.filter((d) => d.dictionaryId === dictionaryId).map((d) => d.direction);
  },

  /**
   * @param {string} methodId
   *
   * @returns {string}
   */
  getLanguageGroupByMethodId(methodId) {
    return this.list.find((d) => d.methodId === methodId)?.languageGroup;
  },

  /**
   * @param {string} direction
   * @param {string} level
   *
   * @returns {string}
   */
  getReverseDirectionByDirectionAndLevel(direction, level) {
    return this.list.find((d) => d.direction === direction && d.level[level])?.reverseDirection;
  }
};

export default Dictionaries;
